import React, { useState } from 'react';
import { Form, Button, Alert, Spinner, ListGroup, Card, Badge } from 'react-bootstrap';

const EMPTY_MAPPING = {
  pipelineJobId1: '',
  pipelineJobId2: '',
  workflowJobId: '',
  bfeJobId: '',
  ipsnetJobId: '',
  s3ImageBucketPath: ''
};

/**
 * PipelineInfoStep - Step 5: Enter pipeline job IDs and BFE info.
 * Supports multiple job mappings per customer (Requirement 18).
 */
const PipelineInfoStep = ({ wizard, api }) => {
  const [mappings, setMappings] = useState([{ ...EMPTY_MAPPING }]);
  const [bfeSearch, setBfeSearch] = useState('');
  const [bfeResults, setBfeResults] = useState([]);
  const [ipsnetSearch, setIpsnetSearch] = useState('');
  const [ipsnetResults, setIpsnetResults] = useState([]);
  const [activeMappingIndex, setActiveMappingIndex] = useState(0);
  const [validationError, setValidationError] = useState('');
  const [complete, setComplete] = useState(false);
  const [ipsnetSearched, setIpsnetSearched] = useState(false);
  const [bfeSearched, setBfeSearched] = useState(false);
  const [searching, setSearching] = useState(false);

  const updateMapping = (index, field, value) => {
    setMappings(prev => prev.map((m, i) => i === index ? { ...m, [field]: value } : m));
  };

  const addMapping = () => {
    setMappings(prev => [...prev, { ...EMPTY_MAPPING }]);
    setActiveMappingIndex(mappings.length);
  };

  const removeMapping = (index) => {
    if (mappings.length <= 1) return;
    setMappings(prev => prev.filter((_, i) => i !== index));
    if (activeMappingIndex >= mappings.length - 1) {
      setActiveMappingIndex(Math.max(0, mappings.length - 2));
    }
  };

  const handleBfeSearch = async () => {
    if (bfeSearch.length < 2) return;
    try {
      setSearching(true);
      setBfeSearched(true);
      const result = await api.searchBfeJobs(wizard.sessionId, bfeSearch);
      setBfeResults(result.jobs || []);
    } catch {
      // Error shown by hook
    } finally {
      setSearching(false);
    }
  };

  const handleIpsnetSearch = async () => {
    if (ipsnetSearch.length < 2) return;
    try {
      setSearching(true);
      setIpsnetSearched(true);
      const result = await api.searchIpsnetJobs(wizard.sessionId, ipsnetSearch);
      setIpsnetResults(result.jobs || []);
    } catch {
      // Error shown by hook
    } finally {
      setSearching(false);
    }
  };

  const handleIpsnetSelect = (jobPair) => {
    updateMapping(activeMappingIndex, 'pipelineJobId1', jobPair.mainJobId.toString());
    if (jobPair.vpJobId) updateMapping(activeMappingIndex, 'pipelineJobId2', jobPair.vpJobId.toString());
    if (jobPair.workflowJobId) updateMapping(activeMappingIndex, 'workflowJobId', jobPair.workflowJobId.toString());
    if (jobPair.mainJobId) updateMapping(activeMappingIndex, 'ipsnetJobId', jobPair.mainJobId.toString());
    if (jobPair.imageS3Path) updateMapping(activeMappingIndex, 's3ImageBucketPath', jobPair.imageS3Path);
    setIpsnetResults([]);
    setIpsnetSearched(false);
  };

  const handleBfeSelect = (job) => {
    updateMapping(activeMappingIndex, 'workflowJobId', job.workflowJobId.toString());
    updateMapping(activeMappingIndex, 'bfeJobId', job.jobId.toString());
    updateMapping(activeMappingIndex, 's3ImageBucketPath', `${job.awsBucketName}/${job.imageBucketFolder}`);
    setBfeResults([]);
    setBfeSearched(false);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setValidationError('');

    for (let i = 0; i < mappings.length; i++) {
      const m = mappings[i];
      const pj1 = parseInt(m.pipelineJobId1);
      const pj2 = parseInt(m.pipelineJobId2);

      if (!pj1 || !pj2 || !parseInt(m.workflowJobId)) {
        setValidationError(`Mapping ${i + 1}: Pipeline Job IDs and Workflow Job ID are required`);
        return;
      }
      if (pj1 === pj2) {
        setValidationError(`Mapping ${i + 1}: Main and VP Pipeline Job IDs must be different`);
        return;
      }
    }

    setComplete(true);
    wizard.completeStep(5, JSON.stringify({ mappings }));
  };

  if (complete) {
    return (
      <Alert variant="success">
        <Alert.Heading>Pipeline Information Saved ({mappings.length} mapping{mappings.length > 1 ? 's' : ''})</Alert.Heading>
        {mappings.map((m, i) => (
          <div key={i} className="mb-2">
            <strong>Mapping {i + 1}:</strong> Main: {m.pipelineJobId1}, VP: {m.pipelineJobId2}, Workflow: {m.workflowJobId}
            {m.bfeJobId && <>, BFE: {m.bfeJobId}</>}
          </div>
        ))}
        <p className="text-muted mb-0">Click "Next" to continue.</p>
      </Alert>
    );
  }

  return (
    <div>
      <h5>Step 5: Pipeline Information</h5>
      <p className="text-muted">Enter pipeline job IDs for each mapping. Use search to auto-fill from ipsnet/BFE.</p>

      {api.error && <Alert variant="danger" dismissible onClose={api.clearError}>{api.error}</Alert>}
      {validationError && <Alert variant="warning">{validationError}</Alert>}

      <Alert variant="info" className="mb-3">
        <small>⚠ Pipeline Job IDs differ between environments. Ensure you are using the correct IDs for this environment.</small>
      </Alert>

      {/* Search Section */}
      <Card className="mb-3">
        <Card.Header>Search (applies to Mapping {activeMappingIndex + 1})</Card.Header>
        <Card.Body>
          {/* Ipsnet Search */}
          <Form.Group className="mb-3">
            <Form.Label>Search ipsnet Jobs (Pipeline IDs)</Form.Label>
            <div className="d-flex gap-2">
              <Form.Control
                type="text"
                value={ipsnetSearch}
                onChange={(e) => setIpsnetSearch(e.target.value)}
                placeholder="Search by customer/job name (min 2 chars)..."
              />
              <Button variant="outline-primary" onClick={handleIpsnetSearch} disabled={ipsnetSearch.length < 2 || searching}>
                {searching ? <Spinner size="sm" /> : 'Search'}
              </Button>
            </div>
          </Form.Group>

          {ipsnetResults.length > 0 && (
            <ListGroup className="mb-3" style={{ maxHeight: '200px', overflow: 'auto' }}>
              {ipsnetResults.map(pair => (
                <ListGroup.Item key={pair.mainJobId} action onClick={() => handleIpsnetSelect(pair)}>
                  <strong>{pair.mainJobName}</strong> — Main ID: {pair.mainJobId}
                  {pair.vpJobId && <>, VP ID: {pair.vpJobId}</>}
                </ListGroup.Item>
              ))}
            </ListGroup>
          )}
          {ipsnetSearched && ipsnetResults.length === 0 && !searching && (
            <Alert variant="light" className="text-center text-muted border mb-3">
              No ipsnet jobs found matching "{ipsnetSearch}"
            </Alert>
          )}

          {/* BFE Search */}
          <Form.Group className="mb-3">
            <Form.Label>Search BFE Jobs</Form.Label>
            <div className="d-flex gap-2">
              <Form.Control
                type="text"
                value={bfeSearch}
                onChange={(e) => setBfeSearch(e.target.value)}
                placeholder="Search by job name (min 2 chars)..."
              />
              <Button variant="outline-primary" onClick={handleBfeSearch} disabled={bfeSearch.length < 2 || searching}>
                {searching ? <Spinner size="sm" /> : 'Search'}
              </Button>
            </div>
          </Form.Group>

          {bfeResults.length > 0 && (
            <ListGroup className="mb-3" style={{ maxHeight: '200px', overflow: 'auto' }}>
              {bfeResults.map(job => (
                <ListGroup.Item key={job.jobId} action onClick={() => handleBfeSelect(job)}>
                  <strong>{job.jobName}</strong> — Job ID: {job.jobId}, Workflow: {job.workflowJobId}
                </ListGroup.Item>
              ))}
            </ListGroup>
          )}
          {bfeSearched && bfeResults.length === 0 && !searching && (
            <Alert variant="light" className="text-center text-muted border mb-3">
              No BFE jobs found matching "{bfeSearch}"
            </Alert>
          )}
        </Card.Body>
      </Card>

      {/* Mapping Forms */}
      <Form onSubmit={handleSubmit}>
        {mappings.map((mapping, index) => (
          <Card key={index} className={`mb-3 ${index === activeMappingIndex ? 'border-primary' : ''}`}>
            <Card.Header className="d-flex justify-content-between align-items-center" onClick={() => setActiveMappingIndex(index)} style={{ cursor: 'pointer' }}>
              <span>
                <Badge bg={index === activeMappingIndex ? 'primary' : 'secondary'} className="me-2">{index + 1}</Badge>
                Job Mapping {index + 1}
                {mapping.pipelineJobId1 && <small className="text-muted ms-2">(Main: {mapping.pipelineJobId1})</small>}
              </span>
              {mappings.length > 1 && (
                <Button variant="outline-danger" size="sm" onClick={(e) => { e.stopPropagation(); removeMapping(index); }}>
                  ✕
                </Button>
              )}
            </Card.Header>
            {index === activeMappingIndex && (
              <Card.Body>
                <Form.Group className="mb-3">
                  <Form.Label>Pipeline Job ID (Main) *</Form.Label>
                  <Form.Control type="number" value={mapping.pipelineJobId1} onChange={(e) => updateMapping(index, 'pipelineJobId1', e.target.value)} />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Pipeline Job ID (VP) *</Form.Label>
                  <Form.Control type="number" value={mapping.pipelineJobId2} onChange={(e) => updateMapping(index, 'pipelineJobId2', e.target.value)} />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Workflow Job ID *</Form.Label>
                  <Form.Control type="number" value={mapping.workflowJobId} onChange={(e) => updateMapping(index, 'workflowJobId', e.target.value)} />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>BFE Job ID</Form.Label>
                  <Form.Control type="number" value={mapping.bfeJobId} onChange={(e) => updateMapping(index, 'bfeJobId', e.target.value)} />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>S3 Image Bucket Path</Form.Label>
                  <Form.Control type="text" value={mapping.s3ImageBucketPath} onChange={(e) => updateMapping(index, 's3ImageBucketPath', e.target.value)} placeholder="bucket-name/folder-path" />
                </Form.Group>
              </Card.Body>
            )}
          </Card>
        ))}

        <div className="d-flex justify-content-between">
          <Button variant="outline-secondary" onClick={addMapping}>
            + Add Another Job Mapping
          </Button>
          <Button type="submit" variant="primary">Save Pipeline Info</Button>
        </div>
      </Form>
    </div>
  );
};

export default PipelineInfoStep;
